/**
 * Registry telemetry event names + attribute keys (FWX-5881).
 *
 * The Fusus Registry app is a small, public, unauthenticated SPA where the
 * public registers their security cameras with an agency, validates an email,
 * and views camera stats. These constants define the front-end funnel emitted
 * to Faro/PCG via `FsTelemetryService.recordEvent`, so the team can answer:
 *   - is the app up / reachable (session + view events come free from Faro),
 *   - did a camera registration error occur (`registry.submit_failed` carries the HTTP status),
 *   - are people actually completing the registration funnel.
 *
 * Names are dot-namespaced; attribute values are snake_case scalars
 * (FsTelemetryAttributes = Record<string, string | number | boolean>).
 */
export const REGISTRY_EVENT = {
  /** Camera registration form rendered. */
  FORM_VIEWED: 'registry.form_viewed',
  /** Submit pressed (MOU agreed when applicable) — request about to fire. */
  SUBMIT_ATTEMPTED: 'registry.submit_attempted',
  /** Camera registration request resolved successfully. */
  SUBMIT_SUCCEEDED: 'registry.submit_succeeded',
  /** Camera registration request failed. */
  SUBMIT_FAILED: 'registry.submit_failed',
  /** Email-validation view opened (link from a confirmation email). */
  EMAIL_VALIDATION_VIEWED: 'registry.email_validation_viewed',
  /** Email validation succeeded. */
  EMAIL_VALIDATION_SUCCEEDED: 'registry.email_validation_succeeded',
  /** Email validation failed. */
  EMAIL_VALIDATION_FAILED: 'registry.email_validation_failed',
  /** Public camera-stats / counter view rendered. */
  STATS_VIEWED: 'registry.stats_viewed',
} as const;

export type RegistryEvent = (typeof REGISTRY_EVENT)[keyof typeof REGISTRY_EVENT];

/** Attribute keys carried on registry events. */
export const REGISTRY_ATTR = {
  /** Deployment region, e.g. 'US' | 'UK'. */
  REGION: 'region',
  /** HTTP status code on a failed request (0 when unknown / network). */
  STATUS: 'status',
} as const;
